import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { usePRD3010_R01Store } from "./Store_PRD3010_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultPRD3010_R01HeaderDto, PRD3010_R01HeaderDto } from "./Dto_PRD3010_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PRD3010_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<PRD3010_R01HeaderDto> | null>;
	handleCreateClick: () => void;
	defaultPRD3010_R01HeaderRow: PRD3010_R01HeaderDto;
}

export const Table_PRD3010_R01 = (props: Props) => {

	const { gridRef, handleCreateClick, defaultPRD3010_R01HeaderRow } = props;

	const { PRD3010_R01List, 
		selectedPRD3010_R01, setSelectedPRD3010_R01, initSelectedPRD3010_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop, setLotScrollTop, setInspectScrollTop,
	} = usePRD3010_R01Store();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'productOrderNo',
		gridData: PRD3010_R01List,
		defaultGridData: defaultPRD3010_R01HeaderRow,
		selectedRow: selectedPRD3010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '사업장',
		orderDt: '지시일자',
		workplaceNm: '공정명',
		itemCd: '품목코드',
		itemNm: '품명',
		resultQty: '생산실적량',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<PRD3010_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{ headerName: '사업장', colId: 'compNm', field: 'compNm', width: 100, 
			cellStyle: (p) => getCellStyle(p, 'compNm'), 
			headerClass: aggridHeaderClass
		},
		{ headerName: '지시일자', colId: 'orderDt', field: 'orderDt', width: 90, 
			cellStyle: (p) => getCellStyle(p, 'orderDt'), 
			
			valueFormatter: (params: any) => params.data?.orderDt ? formatDate(new Date(params.data?.orderDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '공정명', colId: 'workplaceNm', field: 'workplaceNm', width: 80, 
			cellStyle: (p) => getCellStyle(p, 'workplaceNm'), 
			headerClass: aggridHeaderClass
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 80, 
			cellStyle: (p) => getCellStyle(p, 'itemCd'), 
			headerClass: aggridHeaderClass
		},
		{
			headerName: '품목명', colId: 'itemNm', field: 'itemNm', width: 150, 
			cellStyle: (p) => getCellStyle(p, 'itemNm'), 
			headerClass: aggridHeaderClass
		},
		{
			headerName: '생산실적량', colId: 'resultQty', field: 'resultQty', width: 85,
			cellStyle: (p) => getCellStyle(p, 'resultQty'), 
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '지시량', colId: 'orderQty', field: 'orderQty', width: 65,
			cellStyle: (p) => getCellStyle(p, 'orderQty'), 
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PRD3010_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<PRD3010_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);


	// 마운트 시 컬럼 상태 복원
	const handleRowClick = useCallback((e: RowClickedEvent<PRD3010_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 계좌와 같은지 확인
		const isSelected = selectedPRD3010_R01?.productOrderNo === rowData.productOrderNo;
		
		if (isSelected) {
			setSelectedPRD3010_R01(undefined);
			handleCreateClick();
		} else {
			setDetailScrollTop(null);
			setLotScrollTop(null);
			setInspectScrollTop(null);
			setSelectedPRD3010_R01(rowData);
		}
	}, [selectedPRD3010_R01, setSelectedPRD3010_R01, setDetailScrollTop, setLotScrollTop, setInspectScrollTop, handleCreateClick]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: PRD3010_R01List.length.toLocaleString() + "건",
		editable: false,
	}]), [PRD3010_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [PRD3010_R01List, selectedPRD3010_R01]);

	return (
		
		<GridContextWrapper<PRD3010_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedPRD3010_R01}
			defaultRowData={defaultPRD3010_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<PRD3010_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<PRD3010_R01HeaderDto>({
					idKey: 'productOrderNo',
					selectedList: selectedPRD3010_R01 ? [selectedPRD3010_R01.productOrderNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    

				// Grid_Select에 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
